import type { Column, Table } from '@tanstack/vue-table'
import { useResizeObserver } from '@vueuse/core'
import { computed, onMounted, shallowRef } from 'vue'

// TanStack defaults an omitted `size` to 150px. We override that default with a sentinel so
// `getSize()` clamps unsized columns up to `minSize` instead — keeping them out of the table's
// min-width floor (an explicit per-column `size` still wins). Never used as a real width.
export const FLUID_COLUMN_SIZE = -1

// Same story for `maxSize`: the column-sizing feature merges its own default
// (`Number.MAX_SAFE_INTEGER`) into every `columnDef`, so an omitted `maxSize` is NOT
// `undefined` — we own the "uncapped" sentinel via `defaultColumn` so `isFixedWidth`
// can tell a real hard cap from the default. Kept huge so `getSize()` never clamps it.
export const UNCAPPED_MAX_SIZE = Number.MAX_SAFE_INTEGER

// `getSize()` is already clamped to `maxSize`, so equality means the column cannot grow.
function isFixedWidth<TData>(column: Column<TData>): boolean {
  return column.columnDef.maxSize! <= column.getSize()
}

/**
 * Computes per-column pixel widths so that fixed columns (`maxSize` equal to `size`) hold their
 * exact size while the others split the container's spare width **in proportion to their `size`**,
 * each stopping at its `maxSize` and handing its unused share to the rest. Below the natural total
 * the table scrolls via `tableMinWidth` (`max(100%, Σsize px)`).
 *
 * This must be done in JS: `table-fixed` can't express "exact-px caps + proportional fill" in pure
 * CSS (mixed `%`+`px` calc collapses to equal-width, and plain `%` lets capped columns absorb the
 * leftover). See project_datatable_column_sizing memory.
 */
export function useColumnSizing<TData>(table: Table<TData>, container: () => HTMLElement | null | undefined) {
  // Measured directly rather than via `useElementSize`: that helper resets its width to 0 whenever
  // the target element changes, and under SSR hydration that reset can land after the observer's
  // initial callback — leaving the table at its natural widths until the next real resize.
  const containerWidth = shallowRef(0)

  function measure() {
    containerWidth.value = container()?.getBoundingClientRect().width ?? 0
  }

  useResizeObserver(container, measure)
  onMounted(measure)

  const naturalWidth = computed(() => table.getVisibleLeafColumns().reduce((sum, column) => sum + column.getSize(), 0))

  const columnPixelWidths = computed<Record<string, number>>(() => {
    const columns = table.getVisibleLeafColumns()
    const widths: Record<string, number> = {}
    let slack = containerWidth.value - naturalWidth.value
    let growable = columns.filter((column) => !isFixedWidth(column))

    for (const column of columns) {
      widths[column.id] = column.getSize()
    }

    // Water-fill: a column whose proportional share would pass its `maxSize` stops there,
    // and the remaining slack is shared out again among the columns still able to grow.
    while (slack > 0 && growable.length > 0) {
      const grow = growable.reduce((sum, column) => sum + column.getSize(), 0)
      const share = (column: Column<TData>) => (slack * column.getSize()) / grow
      const capped = growable.filter((column) => column.getSize() + share(column) >= column.columnDef.maxSize!)

      if (capped.length === 0) {
        for (const column of growable) {
          widths[column.id] = column.getSize() + share(column)
        }
        break
      }

      for (const column of capped) {
        widths[column.id] = column.columnDef.maxSize!
        slack -= column.columnDef.maxSize! - column.getSize()
      }

      growable = growable.filter((column) => !capped.includes(column))
    }

    return widths
  })

  const tableMinWidth = computed(() => `max(100%, ${naturalWidth.value}px)`)

  return { columnPixelWidths, tableMinWidth }
}
